import { queryKeys, type Unit } from '@mevaser/shared'
import { Field, Input } from '@mevaser/ui'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'

/** What the SIM field holds: the number, and the inventory SIM it matches (if any). */
export interface SimValue {
  iccid: string
  unit: Unit | null
}

export const EMPTY_SIM: SimValue = { iccid: '', unit: null }

/**
 * A SIM at plan purchase (t1375): either picked from the SIMs in stock or typed
 * by hand. Typing a number that is exactly a sellable SIM's number picks that
 * SIM; any other number is kept as typed and saved on the plan only.
 */
export function SimField({
  value,
  onChange,
  label = 'SIM',
}: {
  value: SimValue
  onChange: (next: SimValue) => void
  label?: string
}) {
  const [focused, setFocused] = useState(false)
  const typed = value.iccid.trim()
  const searching = typed.length >= 3 && value.unit === null

  const { data } = useQuery({
    queryKey: queryKeys.units.list({ q: typed, status: 'sellable', sim: true }),
    queryFn: () =>
      api.get<{ items: Unit[] }>('/units', {
        query: { q: typed, status: 'sellable', pageSize: 20 },
      }),
    enabled: searching,
  })
  const matches = (data?.items ?? []).filter((u) => u.productType === 'sim')

  const exact = searching ? matches.find((u) => u.imei === typed) : undefined
  // An exact match is the same thing as picking it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: onChange is a fresh closure each render
  useEffect(() => {
    if (exact) onChange({ iccid: exact.imei, unit: exact })
  }, [exact])

  const hint = value.unit
    ? `From inventory: ${value.unit.productName}. It is sold on this order.`
    : typed
      ? searching && matches.length === 0
        ? 'Not in stock — the number is saved on the plan only.'
        : 'Pick a SIM from stock below, or keep the number as typed.'
      : 'Type the SIM number, or start typing to find one in stock.'

  return (
    <div className="space-y-1.5">
      <Field label={label} hint={hint}>
        {(props) => (
          <Input
            {...props}
            required
            maxLength={40}
            inputMode="numeric"
            autoComplete="off"
            value={value.iccid}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 150)}
            onChange={(e) => onChange({ iccid: e.target.value, unit: null })}
            placeholder="ICCID — scan, type or search"
          />
        )}
      </Field>
      {focused && searching && matches.length > 0 ? (
        <ul className="max-h-40 overflow-auto rounded-md border border-border bg-surface text-sm">
          {matches.map((unit) => (
            <li key={unit.id}>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left hover:bg-surface-muted"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onChange({ iccid: unit.imei, unit })}
              >
                <span>{unit.imei}</span>
                <span className="text-xs text-foreground-muted">
                  {unit.productName}
                  {unit.locationName ? ` · ${unit.locationName}` : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
